/**
 * 工作流商店相关 API
 */
import { get, getFileDownload } from '@/utils/request'
import { refreshUserMemberAndProfile } from '@/utils/userProfileRefresh'

/** 获取工作流列表 */
export async function getWorkflowList(params = {}) {
  return get('/workflow/list', params)
}
/** 工作流类型 */
export async function getWorkTypeList(params={}){
  return get('/workflow/marks', params)
}
/** 获取工作流详情 */
export async function getWorkflowDetail(params={}) {
  return get(`/workflow/detail`, params)
}

/** 批量下载 ZIP 的本地保存文件名（当日日期，不依赖服务端 Content-Disposition） */
function batchWorkflowZipFilename() {
  const d = new Date()
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `【‼️需解压‼️】点石引擎工作流_${y}-${m}-${day}_www.dsagent.cn.zip`
}

const INVALID_FILENAME_CHARS = new Set('<>:"/\\|?*')

/** 去掉无法在常见文件系统中用作文件名的字符 */
function sanitizeWorkflowFileBaseName(raw) {
  let s = String(raw ?? '').trim()
  if (!s) return '工作流'
  s = Array.from(s)
    .map((ch) => {
      const code = ch.charCodeAt(0)
      if (code < 32 || code === 127) return '_'
      if (INVALID_FILENAME_CHARS.has(ch)) return '_'
      return ch
    })
    .join('')
  s = s.replace(/\s+/g, ' ').trim()
  if (!s) return '工作流'
  const maxBase = 120
  if (s.length > maxBase) s = s.slice(0, maxBase).trim()
  return s || '工作流'
}

/** 单个工作流 ZIP 本地保存名：{工作流名称}_点石引擎.dsagent.cn.zip */
function singleWorkflowZipFilename(workflowTitle) {
  const base = sanitizeWorkflowFileBaseName(workflowTitle)
  return `${base}_点石引擎.dsagent.cn.zip`
}

/**
 * 下载工作流压缩包：GET /workflow/download，带 token；批量另存为固定文件名（见 batchWorkflowZipFilename）。
 * @param {{ flowIds: string }} params  - flowIds: 工作流ID，多个用逗号隔开
 */
export async function downloadWorkflow(params = {}) {
  await refreshUserMemberAndProfile()
  return getFileDownload('/workflow/download', params, 'download', batchWorkflowZipFilename())
}

/**
 * 单个工作流下载（GET /workflow/downloadSingle）；另存为固定规则：{工作流名称}_点石引擎.dsagent.cn.zip
 * @param {{ flowId: string, id: string }} params  - flowId: 工作流ID, id: 文件ID（仅这两项会作为查询参数提交）
 * @param {string} [workflowTitle] - 工作流展示名称，用于生成本地文件名
 */
export async function downloadWorkflowSingle(params = {}, workflowTitle = '') {
  await refreshUserMemberAndProfile()
  const flowId = params.flowId != null ? String(params.flowId).trim() : ''
  const id = params.id != null ? String(params.id).trim() : ''
  const forced = singleWorkflowZipFilename(workflowTitle)
  return getFileDownload('/workflow/downloadSingle', { flowId, id }, 'download', forced)
}

/**
 * 收藏工作流（Header：token；Query：flowId）
 * 成功示例：{ code: 0, msg: '操作成功' }
 */
export async function collectWorkflow(params = {}) {
  return get('/workflow/favorite', params)
}

/**
 * 取消收藏（Header：token；Query：flowId）
 * 成功示例：{ code: 0, msg: '操作成功' }
 */
export async function uncollectWorkflow(params = {}) {
  return get('/workflow/cancelFavorite', params)
}

/**
 * 收藏列表（Header：token；Query：pageNum、pageSize 必填；可选 flowName、flowMark、isHot）
 */
export async function getCollectWorkflowList(params = {}) {
  return get('/workflow/favoriteList', params)
}